import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import Loader from '@rsos/components/capstone/Loader';
import { Box, Button, CheckBox, Text } from '@rsos/components/capstone/base';
import { GISServiceTypes } from '@rsos/reduxDataStore';
import { selectGISServices } from '@rsos/reduxDataStore';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { DatasourceManagementState } from '../../state';
import {
  mapLayerSettingsSavingSelector,
  mapLayerSettingsSelector,
  saveMapLayerSettings,
  setMapLayerSettings,
} from '../../state/MapLayerSettingsSlice';
import { SpatialLayer } from '../../state/spatialLayersSlice';
import { DatasourceDispatch } from '../../state/store';
import styles from './MapLayers.module.css';
import useGISToken from './useGISToken';

const MIN_OPACITY = 0;
const MAX_OPACITY = 100;

const clampOpacity = (value: number) =>
  Math.max(MIN_OPACITY, Math.min(MAX_OPACITY, value));

export function MapLayers() {
  useGISToken();
  const dispatch: DatasourceDispatch = useDispatch();

  const gisServices = useSelector(selectGISServices);

  const settings = useSelector(mapLayerSettingsSelector);
  const saving = useSelector(mapLayerSettingsSavingSelector);
  const loading = useSelector<DatasourceManagementState, boolean>(
    state =>
      !state.spatialLayers.initialized ||
      state.gisServices.loading ||
      !state.mapLayerSettings.initialized,
  );
  const spatialLayers = useSelector<DatasourceManagementState, SpatialLayer[]>(
    state => state.spatialLayers.layers,
  );

  const [filter, setFilter] = useState('');
  const normalizedFilter = filter.trim().toLowerCase();

  const filteredGISServices = useMemo(
    () =>
      normalizedFilter
        ? gisServices.filter(layer =>
            (layer.display_name ?? '').toLowerCase().includes(normalizedFilter),
          )
        : gisServices,
    [gisServices, normalizedFilter],
  );

  const filteredSpatialLayers = useMemo(
    () =>
      normalizedFilter
        ? spatialLayers.filter(layer =>
            `${layer.title ?? ''} ${layer.name ?? ''}`
              .toLowerCase()
              .includes(normalizedFilter),
          )
        : spatialLayers,
    [spatialLayers, normalizedFilter],
  );

  const totalCount = gisServices.length + spatialLayers.length;
  const filteredCount =
    filteredGISServices.length + filteredSpatialLayers.length;

  const updateChecked = useCallback(
    (id: number, default_checked: boolean) => {
      const existing = settings[id];
      dispatch(
        setMapLayerSettings({
          ...settings,
          [id]: {
            id,
            default_checked,
            ...(existing?.opacity !== undefined
              ? { opacity: existing.opacity }
              : {}),
          },
        }),
      );
    },
    [settings, dispatch],
  );

  const updateOpacity = useCallback(
    (id: number, opacity: number | undefined) => {
      const existing = settings[id];
      dispatch(
        setMapLayerSettings({
          ...settings,
          [id]: {
            id,
            default_checked: existing?.default_checked ?? false,
            ...(opacity !== undefined ? { opacity } : {}),
          },
        }),
      );
    },
    [settings, dispatch],
  );

  const handleOpacityChange =
    (id: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.currentTarget.value;
      if (raw === '') {
        updateOpacity(id, undefined);
        return;
      }
      const parsed = Number(raw);
      if (Number.isNaN(parsed)) {
        return;
      }
      updateOpacity(id, clampOpacity(parsed));
    };

  const save = useCallback(() => {
    dispatch(saveMapLayerSettings(settings))
      .unwrap()
      .then(() => notifySuccess('Map layer defaults have been saved.'))
      .catch(() => notifyError('Map layer defaults failed to save.'));
  }, [settings, dispatch]);

  const renderOpacityCell = (id: number, isDisabledRow: boolean) => {
    if (isDisabledRow) {
      return <span className={styles.disabledOpacity}>—</span>;
    }
    return (
      <span className={styles.opacityControl}>
        <input
          type="number"
          min={MIN_OPACITY}
          max={MAX_OPACITY}
          step={1}
          value={settings[id]?.opacity ?? ''}
          onChange={handleOpacityChange(id)}
          placeholder="40"
          aria-label="Default layer opacity (0-100)"
          className={styles.opacityInput}
        />
        <span className={styles.opacitySuffix}>%</span>
      </span>
    );
  };

  return (
    <>
      <Box flow={10} mb="20px">
        <Text size="header" fontWeight="semibold" color="primaryText" mb="10px">
          Map Layers
        </Text>
        <Text size="large" color="primaryText">
          Set which layers will be on by default when users log in, and the
          default opacity for each layer (0–100).
        </Text>{' '}
        <Text size="small" color="primaryText">
          <strong>Note:</strong> These controls do not override roles and
          permissions! If a user does not have permission to see a layer, they
          will not see that layer even if it is set to be on by default.
        </Text>
      </Box>

      <div className={styles.card}>
        {loading && (
          <div className={styles.loadingBox}>
            <Loader text="Fetching.." />
          </div>
        )}
        {!loading && (
          <>
            <div className={styles.toolbar}>
              <div className={styles.filterWrapper}>
                <input
                  type="search"
                  value={filter}
                  onChange={e => setFilter(e.currentTarget.value)}
                  placeholder="Filter layers…"
                  aria-label="Filter layers"
                  className={styles.filterInput}
                />
                {filter && (
                  <button
                    type="button"
                    className={styles.clearFilter}
                    onClick={() => setFilter('')}
                    aria-label="Clear filter"
                  >
                    ×
                  </button>
                )}
              </div>
              <span className={styles.count}>
                {normalizedFilter
                  ? `${filteredCount} of ${totalCount} layers`
                  : `${totalCount} layers`}
              </span>
            </div>

            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Layer</th>
                    <th className={styles.checkboxCell}>Default On</th>
                    <th className={styles.opacityCell}>Opacity</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGISServices.length > 0 && (
                    <>
                      <tr className={styles.sectionRow}>
                        <td colSpan={3}>
                          GIS Services ({filteredGISServices.length})
                        </td>
                      </tr>
                      {filteredGISServices.map(layer => {
                        const isBasemap =
                          layer.service_type == GISServiceTypes.basemap;
                        const isGeocoder =
                          layer.service_type == GISServiceTypes.geocode;
                        const isRowDisabled = isBasemap || isGeocoder;
                        return (
                          <tr key={`gis-${layer.id}`}>
                            <td>
                              <span className={styles.layerName}>
                                <span className={styles.layerNamePrimary}>
                                  {layer.display_name}
                                </span>
                                {(isBasemap || isGeocoder) && (
                                  <span className={styles.layerNameSecondary}>
                                    {isBasemap ? 'Basemap' : 'Geocoder'} ·
                                    always on
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className={styles.checkboxCell}>
                              <CheckBox
                                isChecked={
                                  !!settings[layer.id]?.default_checked ||
                                  isBasemap ||
                                  isGeocoder
                                }
                                disabled={isRowDisabled}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  updateChecked(
                                    layer.id,
                                    e.currentTarget.checked,
                                  );
                                }}
                              />
                            </td>
                            <td className={styles.opacityCell}>
                              {renderOpacityCell(layer.id, isRowDisabled)}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}
                  {filteredSpatialLayers.length > 0 && (
                    <>
                      <tr className={styles.sectionRow}>
                        <td colSpan={3}>
                          Spatial Layers ({filteredSpatialLayers.length})
                        </td>
                      </tr>
                      {filteredSpatialLayers.map(layer => (
                        <tr key={`spatial-${layer.id}`}>
                          <td>
                            <span className={styles.layerName}>
                              <span className={styles.layerNamePrimary}>
                                {layer.title}
                              </span>
                              <span className={styles.layerNameSecondary}>
                                {layer.name}
                                {layer.auto_display ? ' · always on' : ''}
                              </span>
                            </span>
                          </td>
                          <td className={styles.checkboxCell}>
                            <CheckBox
                              disabled={layer.auto_display}
                              isChecked={
                                !!settings[layer.id]?.default_checked ||
                                layer.auto_display
                              }
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                updateChecked(
                                  layer.id,
                                  e.currentTarget.checked,
                                );
                              }}
                            />
                          </td>
                          <td className={styles.opacityCell}>
                            {renderOpacityCell(layer.id, layer.auto_display)}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                  {filteredCount === 0 && (
                    <tr>
                      <td colSpan={3} className={styles.emptyState}>
                        No layers match “{filter}”
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.footer}>
              <Button btnType="success" isLoadingProp={saving} onClick={save}>
                Save
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
